import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { decodeStringsInObject } from 'common/utils/objectStringsAction'

import TextInput from 'components/Form/TextInput/TextInput'
import Button from 'components/Form/Button/Button'
import Toast from 'components/Toast/Toast'

import aiIcon from './i/ai.svg'

import { STATUS, SERVER_URL, STATUS_INTERVAL_MS } from './constants'

import styles from './AiTools.module.scss'

const Preview = ({ timeline = [] }) => {
    const [text, setText] = useState('')

    useEffect(() => {
        if (!timeline.length) return
        const last = timeline[timeline.length - 1]
        setText(`${last.type} | ${last.name || last.phase}`)
    }, [timeline])

    if (!text) return null

    return <div className={styles.preview}>{text}</div>
}

const AiTools = ({ onAddBlock }) => {
    const { isDevMode } = useSelector(state => state)

    const [prompt, setPrompt] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const statusInterval = useRef()

    const [statusRequest, setStatusRequest] = useState(null)
    const [resultRequest, setResultRequest] = useState(null)

    const sendJob = async () => {
        try {
            setIsLoading(true)
            const response = await fetch(`${SERVER_URL}/jobs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ prompt }),
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const json = await response.json()
            statusInterval.current = setInterval(() => getStatus(json.jobId), STATUS_INTERVAL_MS)
        } catch (err) {
            console.error(err)
            setIsLoading(false)
        }
    }

    const getStatus = async jobId => {
        try {
            const response = await fetch(`${SERVER_URL}/jobs/${jobId}/status`)

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const json = await response.json()

            if (json.status !== STATUS.RUNNING) {
                clearInterval(statusInterval.current)
                setIsLoading(false)
            }
            if (json.status === STATUS.FAILED) {
                Toast('error', {
                    message: 'Generation error. Please try again.',
                })
            }
            if (json.status === STATUS.SUCCEEDED) {
                getResult(jobId)
            }

            setStatusRequest(json)
        } catch (err) {
            console.error(err)
        }
    }

    const getResult = async jobId => {
        try {
            const response = await fetch(`${SERVER_URL}/jobs/${jobId}/result`)

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const json = await response.json()
            setResultRequest(json)
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        if (!resultRequest) return
        console.log('AI result:', resultRequest.result)
        const addedTypes = []
        resultRequest.result.blocks.forEach(({ content }) => {
            onAddBlock(content.t, null, decodeStringsInObject(content))
            addedTypes.push(content.t)
        })
        Toast('success', { title: `${addedTypes.length} block(s) added!` })
    }, [resultRequest])

    if (!isDevMode) return null

    return (
        <div className={styles.aiTools}>
            {statusRequest?.status === STATUS.RUNNING ? (
                <div className={styles.info}>
                    <div className={styles.placeholder}>
                        Generating... <img src={aiIcon} alt="ai" />
                    </div>
                    <Preview timeline={statusRequest.timelinePreview} />
                </div>
            ) : (
                <>
                    <TextInput
                        placeholder="Create trivia about animals with 2 questions"
                        value={prompt}
                        onUpdate={v => setPrompt(v)}
                        className={styles.inputContainer}
                        inputClassName={styles.input}
                    />
                    <Button
                        className={styles.button}
                        isLoading={isLoading}
                        isDisabled={isLoading || !prompt}
                        onClick={sendJob}
                    >
                        Generate
                    </Button>
                </>
            )}
        </div>
    )
}

export default AiTools
